import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  saveProjectEstimate as saveFirebaseProjectEstimate,
  updateProjectEstimate as updateFirebaseProjectEstimate,
  deleteProjectEstimate as deleteFirebaseProjectEstimate,
  getProjectEstimate as getFirebaseProjectEstimate,
  getAllProjectEstimates as getAllFirebaseProjectEstimates,
  getUserProjectEstimates as getUserFirebaseProjectEstimates,
  getAllUserProjectEstimates as getAllUserFirebaseProjectEstimates,
  getUserEmailById
} from './firebase';
import { useAuth } from './AuthContext';
import { Feature, ProjectEstimate } from './types';
import { generateSlug } from './utils';

interface ProjectContextType {
  projects: ProjectEstimate[];
  currentProject: ProjectEstimate | null;
  loading: boolean;
  saveProject: (name: string, description: string, features: Feature[], componentCount: number, isMigration?: boolean, migrationType?: string, postTypeCount?: number, isContentUpload?: boolean, pageCount?: number, contentUploadLevel?: 'light' | 'medium' | 'heavy') => Promise<string>;
  updateProject: (id: string, data: Partial<ProjectEstimate>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  loadProject: (id: string) => Promise<void>;
  loadProjectBySlug: (slug: string) => Promise<void>;
  createNewProject: () => void;
  isCurrentUserOwner: (projectUserId: string) => boolean;
  syncWithFirebase: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType>({
  projects: [],
  currentProject: null,
  loading: true,
  saveProject: async () => '',
  updateProject: async () => {},
  deleteProject: async () => {},
  loadProject: async () => {},
  loadProjectBySlug: async () => {},
  createNewProject: () => {},
  isCurrentUserOwner: () => false,
  syncWithFirebase: async () => {}
});

export const useProjects = () => useContext(ProjectContext);

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [projects, setProjects] = useState<ProjectEstimate[]>([]);
  const [currentProject, setCurrentProject] = useState<ProjectEstimate | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  // Load projects - always use Firebase when user is authenticated
  useEffect(() => {
    const loadProjects = async () => {
      setLoading(true);
      try {
        if (currentUser) {
          // User is logged in - use Firebase as source of truth
          try {
            // Get all projects from Firebase, not just the user's own projects
            const firebaseProjects = await getAllUserFirebaseProjectEstimates();
            setProjects(firebaseProjects);
          } catch (firebaseError) {
            console.error('Failed to load projects from Firebase:', firebaseError);
            setProjects([]);
          }
        } else {
          // User is not logged in - no projects to show
          setProjects([]);
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [currentUser]);

  // Helper function to check if current user is the owner of a project
  const isCurrentUserOwner = (projectUserId: string): boolean => {
    return currentUser?.uid === projectUserId;
  };

  const saveProject = async (
    name: string, 
    description: string, 
    selectedFeatures: Feature[], 
    componentCount: number,
    isMigration: boolean = false,
    migrationType: string = 'wordpress-wordpress',
    postTypeCount: number = 0,
    isContentUpload: boolean = false,
    pageCount: number = 0,
    contentUploadLevel: 'light' | 'medium' | 'heavy' = 'light'
  ): Promise<string> => {
    if (!currentUser) throw new Error('User not authenticated');
    
    // Generate a base slug from the project name
    let baseSlug = generateSlug(name);
    let slug = baseSlug;
    
    // Check for duplicate slugs and add a suffix if needed
    let slugSuffix = 0;
    let isDuplicateSlug = true;
    
    // Get all existing projects to check for duplicate slugs
    let existingProjects: ProjectEstimate[] = [];
    
    try {
      existingProjects = await getAllFirebaseProjectEstimates();
    } catch (error) {
      console.warn('Error getting existing projects for slug check:', error);
      // If we can't get projects, just use a random suffix
      slug = `${baseSlug}-${Math.random().toString(36).substring(2, 7)}`;
      isDuplicateSlug = false;
    }
    
    // Check if slug exists and add suffix if needed
    while (isDuplicateSlug) {
      // Check if the current slug already exists
      const slugExists = existingProjects.some(project => project.slug === slug);
      
      if (slugExists) {
        // If slug exists, increment suffix and try again
        slugSuffix++;
        slug = `${baseSlug}-${slugSuffix}`;
      } else {
        // No duplicate found, we can use this slug
        isDuplicateSlug = false;
      }
      
      // Safety check to prevent infinite loops
      if (slugSuffix > 100) {
        // If we somehow get here, just add a random string
        slug = `${baseSlug}-${Math.random().toString(36).substring(2, 7)}`;
        break;
      }
    }
    
    const projectData: ProjectEstimate = {
      name,
      description,
      userId: currentUser.uid,
      userEmail: currentUser.email || undefined,
      selectedFeatures,
      componentCount,
      isMigration,
      migrationType,
      postTypeCount,
      isContentUpload,
      pageCount,
      contentUploadLevel,
      slug,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      // Save to Firebase
      const projectId = await saveFirebaseProjectEstimate(projectData);
      
      const savedProject = {
        ...projectData,
        id: projectId
      };
      
      setProjects(prev => [...prev, savedProject]);
      setCurrentProject(savedProject);
      
      return projectId;
    } catch (error) {
      console.error('Error saving project:', error);
      throw error;
    }
  };

  const updateProject = async (id: string, data: Partial<ProjectEstimate>): Promise<void> => {
    try {
      // Check if id is valid
      if (!id) {
        throw new Error("Invalid project ID");
      }
      
      // Get the current project to preserve its slug
      let currentProjectData: ProjectEstimate | null = null;
      
      // Try to get the current project data first to preserve important fields like slug
      try {
        currentProjectData = await getFirebaseProjectEstimate(id);
      } catch (error) {
        console.warn('Could not get current project data:', error);
      }
      
      // Make sure we preserve the slug in the update
      const updateData = { 
        ...data,
        updatedAt: new Date(),
        // Preserve the slug if it exists in the current project
        slug: currentProjectData?.slug || data.slug
      };
      
      // Update in Firebase
      await updateFirebaseProjectEstimate(id, updateData);
      
      // Update local state
      setProjects(prev => 
        prev.map(project => 
          project.id === id ? { ...project, ...updateData } : project
        )
      );
      
      if (currentProject?.id === id) {
        setCurrentProject(prev => 
          prev ? { ...prev, ...updateData } : prev
        );
      }
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  const deleteProject = async (id: string): Promise<void> => {
    try {
      // Delete from Firebase
      await deleteFirebaseProjectEstimate(id);
      
      // Update local state
      setProjects(prev => prev.filter(project => project.id !== id));
      
      if (currentProject?.id === id) {
        setCurrentProject(null);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  };

  const loadProject = async (id: string): Promise<void> => {
    try {
      const firebaseProject = await getFirebaseProjectEstimate(id);
      if (firebaseProject) {
        // If the project doesn't have a userEmail but has a userId, try to fetch the email
        if (firebaseProject.userId && !firebaseProject.userEmail) {
          try {
            const email = await getUserEmailById(firebaseProject.userId);
            if (email) {
              // Update the project with the email
              await updateProject(id, { userEmail: email });
              firebaseProject.userEmail = email;
            }
          } catch (error) {
            console.error('Error fetching user email for project:', error);
          }
        }
        
        setCurrentProject(firebaseProject);
      } else {
        console.warn(`Project with ID ${id} not found`);
      }
    } catch (error) {
      console.error(`Error loading project with ID ${id}:`, error);
      throw error;
    }
  };

  const loadProjectBySlug = async (slug: string): Promise<void> => {
    try {
      // Get all projects and find the one with matching slug
      const allProjects = await getAllFirebaseProjectEstimates();
      const projectWithSlug = allProjects.find(project => project.slug === slug);
      
      if (projectWithSlug && projectWithSlug.id) {
        // We found the project, now load it by ID which is more reliable
        await loadProject(projectWithSlug.id);
      } else {
        console.warn(`Project with slug ${slug} not found`);
        throw new Error(`Project with slug ${slug} not found`);
      }
    } catch (error) {
      console.error('Error loading project by slug:', error);
      throw error;
    }
  };

  const createNewProject = () => {
    setCurrentProject(null);
  };

  // Function to explicitly sync all local projects to Firebase
  const syncWithFirebase = async (): Promise<void> => {
    if (!currentUser) throw new Error('User not authenticated');
    
    try {
      // Refresh projects list from Firebase - get all projects
      const firebaseProjects = await getAllUserFirebaseProjectEstimates();
      setProjects(firebaseProjects);
    } catch (error) {
      console.error('Error syncing with Firebase:', error);
      throw error;
    }
  };

  // Function to update existing projects with user emails
  const updateProjectsWithEmails = async () => {
    if (!currentUser) return;
    
    try {
      // Get all projects owned by the current user
      const userProjects = projects.filter(project => project.userId === currentUser.uid);
      
      // Update each project to include the user's email
      for (const project of userProjects) {
        if (project.id && !project.userEmail && currentUser.email) {
          await updateProject(project.id, { userEmail: currentUser.email });
        }
      }
    } catch (error) {
      console.error('Error updating projects with emails:', error);
    }
  };

  // Call this function when projects are loaded and user is authenticated
  useEffect(() => {
    if (currentUser && projects.length > 0) {
      updateProjectsWithEmails();
    }
  }, [currentUser, projects.length]);

  const value = {
    projects,
    currentProject,
    loading,
    saveProject,
    updateProject,
    deleteProject,
    loadProject,
    loadProjectBySlug,
    createNewProject,
    isCurrentUserOwner,
    syncWithFirebase
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}; 